import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import UIEventBus from '../EventBus';

interface FullscreenToggleProps {}

/**
 * Floating button that maximises the inner site iframe to fill the viewport
 * and hides the 3D scene. Clicking again restores the 3D view.
 *
 * Portaled to document.body so it escapes any parent stacking/pointer-events.
 */
const FullscreenToggle: React.FC<FullscreenToggleProps> = () => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [hovered, setHovered] = useState(false);

    useEffect(() => {
        UIEventBus.on('fullscreenMonitorChanged', (data: any) => {
            setIsFullscreen(!!data?.fullscreen);
        });
    }, []);

    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const next = !isFullscreen;
        setIsFullscreen(next);
        UIEventBus.dispatch('toggleFullscreenMonitor', { fullscreen: next });
    };

    const label = isFullscreen ? 'Exit fullscreen' : 'Maximise inner site';

    const button = (
        <button
            id="prevent-click"
            aria-label={label}
            title={label}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                ...styles.button,
                opacity: hovered ? 1 : 0.92,
                zIndex: isFullscreen ? 10000 : 9500,
            }}
        >
            {isFullscreen ? (
                // minimise icon (arrows pointing in)
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="square"
                >
                    <path d="M9 3 v6 h-6" />
                    <path d="M15 3 v6 h6" />
                    <path d="M9 21 v-6 h-6" />
                    <path d="M15 21 v-6 h6" />
                </svg>
            ) : (
                // maximise icon (arrows pointing out)
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="square"
                >
                    <path d="M3 9 v-6 h6" />
                    <path d="M21 9 v-6 h-6" />
                    <path d="M3 15 v6 h6" />
                    <path d="M21 15 v6 h-6" />
                </svg>
            )}
        </button>
    );

    // Portal to body so the button is never clipped, hidden, or blocked
    // by the #ui div's pointer-events or overflow settings.
    if (typeof document === 'undefined') return null;
    return ReactDOM.createPortal(button, document.body);
};

interface StyleSheetCSS {
    [key: string]: React.CSSProperties;
}

const styles: StyleSheetCSS = {
    button: {
        position: 'fixed',
        top: 16,
        right: 16,
        width: 40,
        height: 40,
        padding: 0,
        background: '#000',
        border: '2px solid #fff',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxSizing: 'border-box',
        pointerEvents: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
    },
};

export default FullscreenToggle;
